import React, {ReactElement} from "react"
import {AllowedTransactionsColumns, TransactionsColumns} from "./Transactions.types"
import {prettyPrintType, Transaction} from "../../resources/transaction"
import {Resource} from "../../resources/common"
import {useNavigate} from "react-router-dom"
import numeral from "numeral"
import {isOrg} from "../../services/orgAuth"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {OrgName} from "../OrgName/OrgName"
import classNames from "classnames"
import moment from "moment"
import IdLink from "../IdLink/IdLink"

function generateSummary(summary: string) {
	const splitByPipe = summary.split("|")

	return (
		<>
			{splitByPipe.map((summaryPart, index) => {
				return (
					<span key={index}>
						<span>{summaryPart}</span>
						{index < splitByPipe.length - 1 ? <span className="has-text-grey-light">|</span> : null}
					</span>
				)
			})}
		</>
	)
}

export default function TransactionRow({
	tx,
	include,
	includedColumns,
	onSelect,
}: {
	tx: Transaction
	include?: Resource[]
	showOrgColumn?: boolean
	includedColumns: Array<AllowedTransactionsColumns>
	onSelect?: (tx: Transaction) => void
}) {
	const navigate = useNavigate()
	const isOrgTransaction = isOrg()
	const id = tx.id
	const org = tx.relationships.org?.data.id
	const accountId = tx.relationships.account.data.id
	const customerId = tx.relationships.customer?.data.id
	const amount = tx.attributes.amount
	const balance = tx.attributes.balance
	const summary = generateSummary(tx.attributes.summary)
	const createdAt = tx.attributes.createdAt
	const navigateUrl = `/${isOrgTransaction ? "org-" : ""}transaction/${tx.relationships.account.data.id}/${id}`
	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedTransactionsColumns, ReactElement>> = {}

	if (includedColumns.includes(TransactionsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className="data-table-id">
				<IdLink className="data-table-id" href={navigateUrl}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(TransactionsColumns.org) && org) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} />
	}

	if (includedColumns.includes(TransactionsColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(TransactionsColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(TransactionsColumns.type)) {
		contentColumns["Type"] = <DataTableCell>{prettyPrintType(tx)}</DataTableCell>
	}

	if (includedColumns.includes(TransactionsColumns.summary)) {
		contentColumns["Summary"] = <DataTableCell style={{whiteSpace: "pre-wrap"}}>{summary}</DataTableCell>
	}

	if (includedColumns.includes(TransactionsColumns.balance)) {
		contentColumns["Balance"] = (
			<DataTableCell className={classNames(tx.attributes.balance >= 0 ? "" : "has-text-danger")}>
				{numeral(balance / 100).format("$0,0.00")}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(TransactionsColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell
				className={classNames(
					tx.attributes.direction === "Credit" && "has-text-success",
					tx.attributes.direction === "Debit" && "has-text-danger"
				)}
			>
				{numeral(amount / 100).format("$0,0.00")}{" "}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(TransactionsColumns.createdAt)) {
		contentColumns["Created At"] = (
			<DataTableCell style={{whiteSpace: "nowrap"}}>{moment(createdAt).format("L LT")}</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			className={classNames(onSelect && "clickable-row")}
			onClick={(e) => {
				e.preventDefault()
				if (onSelect) {
					onSelect(tx)
				} else {
					navigate(navigateUrl)
				}
			}}
		>
			{content}
		</DataTableRow>
	)
}
